import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { RoofFramingType, type RoofTruss } from "building-types/framing/roofFramingTypes";
import type { RoofTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { RoofTrussType } from "building-types/framing/roofTrussTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew } from "doc/utils/lumberTools";
import { planeNormal } from "geom/plane";
import type { TrussGenerator } from "./trussGenerator";
import {
  generateBottomChord,
  generateCenterVerticalWebChord,
  generateTopChords,
  projectChordsToFramingPlane,
  setupBottomChordRails,
  setupTopChordRails,
  setupWebChordRails,
} from "./trussUtils";

export class KingPostTrussGenerator implements TrussGenerator {
  generate(input: RoofTrussBasicInput): RoofTruss {
    const {
      leftSpan,
      leftEaveFasciaSize,
      rightSpan,
      rightEaveFasciaSize,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordProfile,
      bottomChordProfile,
      webChordProfile,
      ridgeRafterProfile,
      bottomChordOffset,
      trussPlane,
    } = input;

    if (Array.isArray(leftSpan) || Array.isArray(rightSpan)) {
      throw new Error("King post truss does not support multiple spans");
    }

    const ridgeRafterHalfHeight = unsignedNumberNew(LumberProfileCatalog[ridgeRafterProfile].width / 2);
    const webChordHalfHeight = unsignedNumberNew(LumberProfileCatalog[webChordProfile].width / 2);
    const bottomChordHeight = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].width);
    const topChordHeight = unsignedNumberNew(LumberProfileCatalog[topChordProfile].width);

    const { left, right } = generateTopChords(leftSpan, rightSpan, leftTopChordPitch, rightTopChordPitch);
    const {
      bottomChord: bottomChordTopPoints,
      leftHeelChord,
      rightHeelChord,
    } = generateBottomChord(
      leftSpan,
      rightSpan,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
    );

    const webChordCenterPoints = generateCenterVerticalWebChord(
      webChordHalfHeight,
      bottomChordTopPoints[5] - bottomChordHeight / 2,
      ridgeRafterHalfHeight,
    );

    const [
      leftTopChordTopPointsToFramingPlane,
      rightTopChordTopPointsToFramingPlane,
      bottomChordTopPointsToFramingPlane,
      webChordCenterPointsToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
    ] = projectChordsToFramingPlane(
      [left, right, bottomChordTopPoints, webChordCenterPoints, leftHeelChord, rightHeelChord],
      trussPlane,
    );

    const topChords = lumberPiecesByProfileNew(LumberProfileCatalog[topChordProfile], { [topChordProfile]: [] });
    const bottomChords = lumberPiecesByProfileNew(LumberProfileCatalog[bottomChordProfile], {
      [bottomChordProfile]: [],
    });
    const webChords = lumberPiecesByProfileNew(LumberProfileCatalog[webChordProfile], { [webChordProfile]: [] });
    const [normX, normY, normZ] = planeNormal(trussPlane);

    setupTopChordRails(topChords, leftTopChordTopPointsToFramingPlane, leftEaveFasciaSize, normX, normY, normZ);
    setupTopChordRails(topChords, rightTopChordTopPointsToFramingPlane, rightEaveFasciaSize, normX, normY, normZ);

    setupBottomChordRails(bottomChords, bottomChordTopPointsToFramingPlane, normX, normY, normZ);
    [webChordCenterPointsToFramingPlane, leftHeelChordToFramingPlane, rightHeelChordToFramingPlane].forEach(
      (webChord) => {
        setupWebChordRails(webChords, webChord, normX, normY, normZ);
      },
    );

    return {
      trussType: RoofTrussType.KING_POST_TRUSS,
      components: [
        { type: RoofFramingComponentType.TOP_CHORD, lumber: topChords },
        { type: RoofFramingComponentType.BOTTOM_CHORD, lumber: bottomChords },
        { type: RoofFramingComponentType.WEB, lumber: webChords },
      ],
      framingType: RoofFramingType.TRUSS,
      plane: trussPlane,
    };
  }
}
